<script setup lang="ts">
import { tpgIconClose, tpgIconRefresh } from '@tpg/grammar-web-icons';
import { ref } from 'vue';

import type { GrammarMessageStatusBarMessageStatus } from '@nora-monorepo/grammar-ui';
import { GrammarMessageStatusBar, GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import {
  isImage,
  isVideo,
  textSecondary,
} from '@nora-monorepo/ui-shared';

import type { FileUpload } from '#nora-chat-ui/components/open-conversations/messages/file';
import { useAttachmentStore } from '#nora-chat-ui/stores/attachment-store';
import { getIcon } from '#nora-chat-ui/utils/get-extension';

interface MessageUploadProps {
  uploads: FileUpload[];
}

defineProps<MessageUploadProps>();

const attachmentStore = useAttachmentStore();

const previewRenderFailures = ref(new Set<string>());

function onPreviewError(fileId: string): void {
  previewRenderFailures.value.add(fileId);
}

function canShowPreview(file: FileUpload): boolean {
  if (previewRenderFailures.value.has(file.id)) {
    return false;
  }

  return isImage(file.file.type) || isVideo(file.file.type);
}

function getMessageStatus(file: FileUpload): GrammarMessageStatusBarMessageStatus {
  if (file.status === 'file-upload-error') {
    return 'error';
  }

  if (file.progress >= 100 || file.status === 'success') {
    return 'delivered';
  }

  return 'sending';
}
</script>

<template>
  <div
    v-if="uploads.length > 0"
    class="flex flex-row overflow-x-scroll prevent-shrink pb-3 gap-2 disable-scrollbars min-h-24"
  >
    <div
      v-for="(file, index) in uploads"
      :key="index"
      class="relative overflow-hidden shrink-0 w-24 h-24"
    >
      <div
        v-if="file.status === 'file-upload-error'"
        class="bg-light-bg-tertiary/60 rounded-md p-0.5 absolute flex flex-col items-center justify-end w-full h-full"
      >
        <h2 class="text-light-primary text-center">
          Upload failed
        </h2>

        <span class="mi warning text-orange-light" />

        <GrammarQuaternaryButton
          :icon-template="tpgIconRefresh"
          icon-color="rgb(15 28 36)"
          size="small"
          @click="attachmentStore.retryUpload(index)"
        />
      </div>

      <img
        v-if="canShowPreview(file)"
        :src="file.preview"
        alt=""
        class="rounded-md w-full h-full object-cover"
        @error="() => onPreviewError(file.id)"
      >

      <div
        v-else
        class="h-full w-full flex items-center justify-center"
      >
        <span
          class="md-48 mi" :class="[textSecondary, getIcon(file.file.type)]"
        />
      </div>

      <div class="absolute bottom-0 left-0 right-0">
        <GrammarMessageStatusBar
          v-if="file.progress < 100 && file.status !== 'file-upload-error'"
          :value="file.progress"
          :message-status="getMessageStatus(file)"
        />
      </div>

      <div class="absolute right-[3px] top-[3px] pointer-events-none">
        <GrammarQuaternaryButton
          class="pointer-events-auto"
          size="small"
          :icon-template="tpgIconClose"
          @click="() => attachmentStore.removeUploadById(file.id)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.disable-scrollbars::-webkit-scrollbar {
  background: transparent; /* Chrome/Safari/Webkit */
  width: 0px;
}
.disable-scrollbars {
  scrollbar-width: none; /* for other browsers like Firefox */
}
</style>
